import React, { useEffect, useMemo, useRef, useState } from "react";
import { UnitStore } from "units/_stores/UnitStore";
import { TUnitListingMdl } from "admin/units/_models/UnitListingMdl";
import { UnitContext } from "units/UnitContext";
import { UnitGallery } from "units/UnitGallery";
import { UnitHeader } from "units/UnitHeader";
import { UnitCheckList } from "units/UnitCheckList";
import { GALLERY_NAME_MODAL, GALLERY_TABS, PROPERTY_OPTION } from "_configs/propertyConfig";
import { useTranslation } from "react-i18next";
import { GalleryListModal } from "units/UnitViewGalleryListModal";
import ShareButton from "properties/propertyPage/propertyView/ShareButton";
import ViewFavoriteButton from "properties/propertyPage/propertyView/ViewFavoriteButton";
import { COMMON_RESOURCE } from "_common/resources/BaseResourceStore";
import close from "../assets/images/icons/closeModal.svg";
import { galleryStore } from "gallery/_store/GalleryStore";
import PropertyOrUnitFloorPlans from "properties/propertyPage/PropertyOrUnitFloorPlans";
import { ADMIN_RESOURCE } from "admin/_common/photos/AdminPhotosStore";
import UnitDescription from "units/UnitDescription";
import { usePropertyStore } from "properties/propertyPage/PropertyContext";
import { TFeature } from "properties/_models/PropertyMdl";
import UnitScrollButtons from "units/UnitScrollButton";
import scrollImg from "../assets/images/icons/scroll.svg";

type Props = {
    unit: TUnitListingMdl;
    onClose: () => void;
};

export function UnitPage({ unit, onClose }: Props) {
    const unitStore = useMemo(() => new UnitStore(unit), [unit]);
    const { property } = usePropertyStore();
    const { t } = useTranslation();
    const floorPlanRef = useRef<HTMLDivElement | null>(null);
    const checklistRef = useRef<HTMLDivElement | null>(null);
    const elementRef = useRef<HTMLDivElement | null>(null);
    const [isAtBottom, setIsAtBottom] = useState(false);

    const scrollToFloorPlan = () => {
        floorPlanRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    const scrollToChecklist = () => {
        checklistRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        galleryStore.setModalName(GALLERY_NAME_MODAL.UNIT);

        return () => {
            galleryStore.setModalName(GALLERY_NAME_MODAL.PROPERTY);
        };
    }, []);

    useEffect(() => {
        const handleScroll = () => {
            const element = elementRef.current;
            if (element) {
                const { scrollTop, scrollHeight, clientHeight } = element;
                const isScrolledToBottom = Math.abs(scrollTop + clientHeight - scrollHeight) < 1;
                setIsAtBottom(isScrolledToBottom);
            }
        };

        const element = elementRef.current;
        if (element) {
            element.addEventListener("scroll", handleScroll);
            return () => {
                element.removeEventListener("scroll", handleScroll);
            };
        }
    }, []);

    function getPropertyFeatures(propertyFeatures: TFeature): { [key in PROPERTY_OPTION]?: boolean } | null {
        const propertyFeatureKeys: PROPERTY_OPTION[] = [
            PROPERTY_OPTION.appliances,
            PROPERTY_OPTION.automatedBlinds,
            PROPERTY_OPTION.blinds,
            PROPERTY_OPTION.muralAirConditioning,
            PROPERTY_OPTION.washerDryerInSuite,
            PROPERTY_OPTION.furnishedEquipped,
        ];

        const matchingFeatures: { [key in PROPERTY_OPTION]?: boolean } = {};

        propertyFeatureKeys.forEach((feature) => {
            if (feature in propertyFeatures && propertyFeatures[feature]) {
                matchingFeatures[feature] = true;
            }
        });

        return Object.keys(matchingFeatures).length > 0 ? matchingFeatures : null;
    }

    return (
        <UnitContext unitStore={unitStore}>
            <div className="my-5 mx-5 relative h-[95vh] overflow-y-auto" ref={elementRef}>
                <div className="flex justify-between">
                    <div className="flex mr-8 sm:mr-0 mb-3 gap-x-2">
                        <ShareButton unitName={unit.name} />
                        <ViewFavoriteButton item={unit} resource={COMMON_RESOURCE.UNIT} />
                    </div>
                    <div className="absolute right-5 top-3 z-60 cursor-pointer">
                        <img src={close} className="h-4" alt="close-modal" onClick={onClose} />
                    </div>
                </div>
                <UnitGallery images={unit.photos ?? []} unit={unit} />
                <UnitHeader unit={unit} />
                <UnitScrollButtons
                    unit={unit}
                    title={t("unitPage.form.title", { unit: unit.title, property: property.localized.title })}
                    scrollToFloorPlan={scrollToFloorPlan}
                    scrollToChecklist={scrollToChecklist}
                />
                {unit.features && (
                    <div ref={checklistRef}>
                        <UnitCheckList
                            title={t("unit.featuresTitle")}
                            datas={Object.entries({
                                ...getPropertyFeatures(property?.features),
                                ...unit.features,
                            }).filter(([key]) => key)}
                        />
                    </div>
                )}
                {unit.description && <UnitDescription description={unit.description} />}
                <div ref={floorPlanRef}>
                    <PropertyOrUnitFloorPlans
                        floorPlans={unit.floorPlans}
                        resource={ADMIN_RESOURCE.UNIT}
                        onClick={() => galleryStore.setOpenedGalleryTab(GALLERY_TABS.LEVEL_PLAN)}
                    />
                </div>
                {!isAtBottom && (
                    <div className="fixed bottom-10 bg-gray-200/75 rounded-full animate-bounce left-1/2 transform -translate-x-1/2 p-3 z-30 shadow-md select-none">
                        <img src={scrollImg} className="h-7 w-7" alt="scrollImage" />
                    </div>
                )}
            </div>
            <GalleryListModal
                galleryImages={[
                    {
                        images: unit.photos,
                        tabName: GALLERY_TABS.GALLERY,
                        label: t("property.galleryNavbar.mainGallery"),
                    },
                    {
                        images: unit.floorPlans,
                        tabName: GALLERY_TABS.LEVEL_PLAN,
                        label: t("property.galleryNavbar.levelPlan"),
                    },
                    {
                        images: property.photos,
                        tabName: GALLERY_TABS.PROPERTY_PHOTOS,
                        label: t("property.galleryNavbar.propertyPhotos"),
                    },
                ]}
                item={unit}
                resource={COMMON_RESOURCE.UNIT}
            />
        </UnitContext>
    );
}
